import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Bell, Save, Mail, MessageSquare, Phone } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReminderSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    days_before_due: 3,
    send_on_due_date: true,
    days_after_due: 2,
    sms_enabled: false,
    email_enabled: true,
    whatsapp_enabled: false,
    include_payment_link: true,
    payment_link_template: 'https://pay.rentkollect.com/tenant/{tenant_id}'
  });

  async function fetchSettings() {
    try {
      const response = await axios.get(`${API}/reminders/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch reminder settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reminders/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Settings Saved",
        description: "Reminder settings updated successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reminder Settings</h1>
          <p className="text-gray-600 mt-1">Configure automatic rent reminders</p>
        </div>
        <Bell size={32} className="text-blue-600" />
      </div>

      {/* Schedule Settings */}
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Reminder Schedule</h2>
        <div className="space-y-4">
          <div>
            <Label>Days Before Due Date</Label>
            <Input
              type="number"
              value={settings.days_before_due}
              onChange={(e) => setSettings({...settings, days_before_due: parseInt(e.target.value)})}
              min="1"
              max="10"
            />
            <p className="text-xs text-gray-600 mt-1">Send reminder X days before rent is due (Industry standard: 3 days)</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.send_on_due_date}
              onChange={(e) => setSettings({...settings, send_on_due_date: e.target.checked})}
              className="w-4 h-4"
            />
            <Label>Send reminder on due date (7th of every month)</Label>
          </div>

          <div>
            <Label>Days After Due Date (Overdue)</Label>
            <Input
              type="number"
              value={settings.days_after_due}
              onChange={(e) => setSettings({...settings, days_after_due: parseInt(e.target.value)})}
              min="1"
              max="10"
            />
            <p className="text-xs text-gray-600 mt-1">Send overdue reminder X days after due date (Industry standard: 2 days)</p>
          </div>
        </div>
      </Card>

      {/* Channel Settings */}
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Reminder Channels</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="text-blue-600" size={24} />
              <div>
                <p className="font-semibold">Email Reminders</p>
                <p className="text-sm text-gray-600">Via Zoho ZeptoMail</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.email_enabled}
              onChange={(e) => setSettings({...settings, email_enabled: e.target.checked})}
              className="w-5 h-5"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Phone className="text-green-600" size={24} />
              <div>
                <p className="font-semibold">SMS Reminders</p>
                <p className="text-sm text-gray-600">Via Twilio (Requires API key)</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.sms_enabled}
              onChange={(e) => setSettings({...settings, sms_enabled: e.target.checked})}
              className="w-5 h-5"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-3">
              <MessageSquare className="text-purple-600" size={24} />
              <div>
                <p className="font-semibold">WhatsApp Reminders</p>
                <p className="text-sm text-gray-600">Via Twilio (Requires API key)</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.whatsapp_enabled}
              onChange={(e) => setSettings({...settings, whatsapp_enabled: e.target.checked})}
              className="w-5 h-5"
            />
          </div>
        </div>
      </Card>

      {/* Payment Link Settings */}
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Payment Link</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.include_payment_link}
              onChange={(e) => setSettings({...settings, include_payment_link: e.target.checked})}
              className="w-4 h-4"
            />
            <Label>Include payment link in reminders</Label>
          </div>

          {settings.include_payment_link && (
            <div>
              <Label>Payment Link Template</Label>
              <Input
                value={settings.payment_link_template}
                onChange={(e) => setSettings({...settings, payment_link_template: e.target.value})}
                placeholder="https://pay.rentkollect.com/tenant/{tenant_id}"
              />
              <p className="text-xs text-gray-600 mt-1">Use {'{tenant_id}'} as placeholder for tenant ID</p>
            </div>
          )}
        </div>
      </Card>

      {/* API Keys Info */}
      <Card className="p-6 bg-yellow-50 border-yellow-200">
        <h3 className="font-bold mb-2">📋 API Keys Required</h3>
        <p className="text-sm text-gray-700 mb-2">To enable SMS and WhatsApp reminders, add these keys to <code className="bg-white px-2 py-1 rounded">/app/backend/.env</code>:</p>
        <div className="text-xs bg-gray-900 text-green-400 p-3 rounded font-mono">
          <p>TWILIO_ACCOUNT_SID=your_sid</p>
          <p>TWILIO_AUTH_TOKEN=your_token</p>
          <p>TWILIO_PHONE_NUMBER=+1234567890</p>
          <p>ZEPTOMAIL_API_KEY=your_key</p>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default ReminderSettings;